import { type Variant } from '../gameTypes.js';
import {
  WAVES_CEILING_Y,
  WAVES_DEFAULT_SPAWN_Y,
  WAVES_FLOOR_Y,
  WAVES_MIN_CORRIDOR_MARGIN,
  WAVES_VARIANT_TUNING,
  type WavesTerrain,
  type WavesTerrainNode,
} from '../wavesChart.js';
import { hashStringToSeed, mulberry32 } from '../seededRng.js';

/**
 * Widest the winding corridor ever opens to (half-height, playfield
 * units). 180 → a 360px corridor inside the 720px playfield. The
 * corridor BREATHES between this and {@link WAVES_CORRIDOR_HALF_NARROW}
 * as the motif sequencer below squeezes and reopens it.
 */
export const WAVES_CORRIDOR_HALF_WIDE = 180;

/**
 * Narrowest the corridor squeezes to (half-height), per variant. The
 * "sliver" extreme a funnel / straight-fly motif drives toward. Tighter
 * on harder tiers, but every value stays clear of the validator's
 * `2r + WAVES_MIN_CORRIDOR_MARGIN` floor (72 / 68 / 64 px wide) with
 * comfortable slack so the gliding ship never jams.
 */
export const WAVES_CORRIDOR_HALF_NARROW: Record<Variant, number> = {
  normal: 80,
  hard: 66,
  extreme: 54,
};

/**
 * Fraction of the per-step ramp-slope budget the midline may consume;
 * the remainder is reserved for the corridor-width ramp. Splitting the
 * budget guarantees BOTH surfaces (floor = mid + half, ceiling =
 * mid − half) individually stay within the slope cap even when the
 * midline and width ramp at once — which keeps the glide-clamp
 * resolution-independent (client per-frame vs server 1/16-beat) over a
 * squeezing corridor.
 */
const WAVES_MID_SLOPE_BUDGET_FRAC = 0.6;
const WAVES_WIDTH_SLOPE_BUDGET_FRAC = 1 - WAVES_MID_SLOPE_BUDGET_FRAC;

/** Midline ramp resolution (beats between terrain vertices). */
export const WAVES_TERRAIN_CADENCE_BEATS = 4;

/**
 * Ramp slope cap as a fraction of the player's vertical speed. Well
 * below 1 so a gliding ship keeps headroom to climb away from a rising
 * floor — the hard determinism bound (slope <= vY) is the validator's;
 * this is the friendlier generation bound.
 */
const WAVES_RAMP_SLOPE_FRAC = 0.42;

/** Below this, a clamped wall step is dropped (treated as no wall). */
const WAVES_MIN_WALL_HEIGHT = 55;

interface WavesWallTuning {
  /** Chance of placing a wall at each eligible cadence vertex. */
  wallProb: number;
  minHeight: number;
  maxHeight: number;
  /** Minimum beats between consecutive walls. */
  minSpacingBeats: number;
}

/**
 * Per-variant wall density + height. Harder variants get taller, more
 * frequent walls — the "climb over the 90-degree face in time" demand
 * escalates with the rest of the difficulty curve. Probability is high
 * and spacing tight enough that walls recur across the WHOLE song rather
 * than clustering near the intro; each eligible cadence vertex (every
 * {@link WAVES_TERRAIN_CADENCE_BEATS} beats, past the approach runway)
 * rolls once and `minSpacingBeats` enforces a readable gap.
 */
const WAVES_WALL_TUNING: Record<Variant, WavesWallTuning> = {
  normal: { wallProb: 0.3, minHeight: 70, maxHeight: 120, minSpacingBeats: 16 },
  hard: { wallProb: 0.4, minHeight: 90, maxHeight: 160, minSpacingBeats: 13 },
  extreme: { wallProb: 0.5, minHeight: 110, maxHeight: 200, minSpacingBeats: 10 },
};

/**
 * Obstacle-free RECOVERY window kept just AFTER a wall. Small on purpose
 * — the reachability filter threads post-wall obstacles from the wall's
 * clear band and silently drops any that are genuinely unreachable, so
 * the only job here is to stop an obstacle landing on (or right on top
 * of) the wall beat where its safe band might not overlap the wall's.
 */
export const WAVES_WALL_RECOVERY_BEATS = 0.5;

/**
 * Obstacle-free APPROACH window kept BEFORE a wall. Sized so the ship
 * can climb across the full corridor within the window — i.e. reach the
 * wall's clear band from ANY prior Y — which keeps the majority of walls
 * reachable and the reachability filter in lockstep with the perfect-
 * replay planner. Asymmetric (no large post-wall runway) so harder
 * variants — which carry more walls — don't strip a disproportionate
 * slice of their obstacle density and invert the monotonic difficulty
 * curve.
 */
export function wallApproachBeats(vY: number): number {
  return (2 * WAVES_CORRIDOR_HALF_WIDE) / vY + 0.5;
}

/** Clamp helper (no shared util in this module). */
function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Beat fraction at which the song's intensity curve peaks. Intensity
 * ramps from a gentle intro up to this point, then eases off into a
 * confident finish — the standard rhythm-platformer arc (climb, climax
 * ~80% through, soft landing) rather than a flat or monotonically-rising
 * grind.
 */
const WAVES_CLIMAX_FRAC = 0.82;
const WAVES_INTENSITY_INTRO = 0.22;
const WAVES_INTENSITY_FINISH = 0.55;

/**
 * Normalised difficulty intent at a given beat (0 = gentlest, 1 =
 * hardest). Pure function of song progress — terrain pacing is kept
 * INDEPENDENT of the osu-derived obstacle layer (which carries its own
 * musical intensity), so the corridor's tension curve reads cleanly on
 * top of the notes. Drives both motif SELECTION (rests early, squeezes
 * and sweeps late) and motif TIGHTNESS (how narrow a squeeze commits to).
 */
export function wavesIntensityAt(beat: number, totalBeats: number): number {
  if (totalBeats <= 0) {
    return 0.5;
  }
  const p = clamp(beat / totalBeats, 0, 1);
  if (p <= WAVES_CLIMAX_FRAC) {
    return lerp(WAVES_INTENSITY_INTRO, 1, p / WAVES_CLIMAX_FRAC);
  }
  return lerp(1, WAVES_INTENSITY_FINISH, (p - WAVES_CLIMAX_FRAC) / (1 - WAVES_CLIMAX_FRAC));
}

/**
 * The corridor's macro-shape vocabulary. Each motif is a self-contained
 * "rhythm group" (a few cadence steps long) realised as a readable
 * Geometry-Dash / Impossible-Game wave shape, rather than the
 * directionless random-walk the corridor used to wander through:
 *
 *   - `rest`     — wide, gently-drifting corridor. The exhale beat that
 *                  separates harder groups (tension-and-release pacing).
 *   - `straight` — a sustained narrow FLAT channel; the player holds a
 *                  near-flat line with controlled micro-taps (the
 *                  signature "straight fly" skill).
 *   - `funnel`   — width converges wide→narrow across the motif: a
 *                  squeeze the player sees coming and pre-positions for.
 *   - `sweep`    — the midline glides to the far side of the playfield
 *                  (alternating each sweep), carving a big S the ship
 *                  rides along.
 *   - `stairs`   — the midline climbs/drops in stepped treads (ramp,
 *                  flat, ramp, flat): a countable, music-syncable rise.
 */
type MotifKind = 'rest' | 'straight' | 'funnel' | 'sweep' | 'stairs';

interface TerrainMotif {
  kind: MotifKind;
  durationBeats: number;
  /** Half-width at motif start (only `funnel` ramps away from this). */
  widthFrom: number;
  /** Half-width the motif drives toward. */
  widthTo: number;
  midMode: 'hold' | 'glide' | 'stairs';
  /** `hold`: the flat line to keep. */
  midHold: number;
  /** `glide`: the (far) midline target to sweep toward. */
  midTarget: number;
  /** `stairs`: base midline + per-tread signed step. */
  midBase: number;
  stairDir: number;
  stairHeight: number;
}

interface MotifContext {
  rng: () => number;
  intensity: number;
  halfWide: number;
  halfNarrow: number;
  /** Current half-width when the motif is picked (funnel converges from here). */
  half: number;
  /** Current midline when the motif is picked. */
  mid: number;
  /** Direction the next `sweep` should travel (+1 = toward floor). */
  sweepDir: number;
  cadence: number;
}

/** Narrowest half-width a `straight` motif commits to at this intensity. */
function straightHalf(ctx: MotifContext): number {
  const loose = ctx.halfWide * 0.62;
  const tight = ctx.halfNarrow * 1.15;
  return clamp(lerp(loose, tight, ctx.intensity), ctx.halfNarrow, ctx.halfWide);
}

/** Tightest half-width a `funnel` converges to at this intensity. */
function funnelHalf(ctx: MotifContext): number {
  const loose = ctx.halfWide * 0.5;
  const tight = ctx.halfNarrow;
  return clamp(lerp(loose, tight, ctx.intensity), ctx.halfNarrow, ctx.halfWide);
}

function restMotif(ctx: MotifContext): TerrainMotif {
  const wide = clamp(lerp(ctx.halfWide * 0.82, ctx.halfWide, ctx.rng()), ctx.halfNarrow, ctx.halfWide);
  // Gentle drift toward a nearby target so the breather isn't dead-flat.
  const drift = (ctx.rng() - 0.5) * 120;
  return {
    kind: 'rest',
    durationBeats: (2 + Math.floor(ctx.rng() * 2)) * ctx.cadence,
    widthFrom: wide,
    widthTo: wide,
    midMode: 'glide',
    midHold: ctx.mid,
    midTarget: ctx.mid + drift,
    midBase: ctx.mid,
    stairDir: 1,
    stairHeight: 0,
  };
}

function straightMotif(ctx: MotifContext): TerrainMotif {
  const half = straightHalf(ctx);
  return {
    kind: 'straight',
    // Longer than other motifs: a flat hold needs room to settle into a
    // rhythm before it reads as a "straight fly" rather than a blip.
    durationBeats: (3 + Math.floor(ctx.rng() * 3)) * ctx.cadence,
    widthFrom: half,
    widthTo: half,
    midMode: 'hold',
    midHold: ctx.mid,
    midTarget: ctx.mid,
    midBase: ctx.mid,
    stairDir: 1,
    stairHeight: 0,
  };
}

function funnelMotif(ctx: MotifContext): TerrainMotif {
  return {
    kind: 'funnel',
    durationBeats: (3 + Math.floor(ctx.rng() * 2)) * ctx.cadence,
    widthFrom: ctx.half,
    widthTo: funnelHalf(ctx),
    midMode: 'hold',
    midHold: ctx.mid,
    midTarget: ctx.mid,
    midBase: ctx.mid,
    stairDir: 1,
    stairHeight: 0,
  };
}

function sweepMotif(ctx: MotifContext): TerrainMotif {
  // Stay fairly open so the big midline swing has room to glide.
  const half = clamp(lerp(ctx.halfWide * 0.7, ctx.halfWide * 0.92, ctx.rng()), ctx.halfNarrow, ctx.halfWide);
  // Aim at the far extreme; the per-step clamp keeps it inside the
  // (width-dependent) reachable midline band as the ship travels.
  const target =
    ctx.sweepDir > 0 ? WAVES_FLOOR_Y - ctx.halfNarrow : WAVES_CEILING_Y + ctx.halfNarrow;
  return {
    kind: 'sweep',
    durationBeats: (2 + Math.floor(ctx.rng() * 2)) * ctx.cadence,
    widthFrom: half,
    widthTo: half,
    midMode: 'glide',
    midHold: ctx.mid,
    midTarget: target,
    midBase: ctx.mid,
    stairDir: 1,
    stairHeight: 0,
  };
}

function stairsMotif(ctx: MotifContext): TerrainMotif {
  const half = clamp(lerp(ctx.halfWide * 0.7, ctx.halfWide * 0.85, ctx.rng()), ctx.halfNarrow, ctx.halfWide);
  return {
    kind: 'stairs',
    durationBeats: (3 + Math.floor(ctx.rng() * 2)) * ctx.cadence,
    widthFrom: half,
    widthTo: half,
    midMode: 'stairs',
    midHold: ctx.mid,
    midTarget: ctx.mid,
    midBase: ctx.mid,
    stairDir: ctx.rng() < 0.5 ? -1 : 1,
    stairHeight: lerp(45, 80, ctx.intensity),
  };
}

/**
 * Pick the next corridor motif. Weights shift with intensity (rests give
 * way to squeezes and sweeps as the song builds) and the kind that just
 * played is suppressed so the corridor keeps varying. After an intense
 * squeeze/sweep there's a strong chance of a rest, so hard groups land on
 * an exhale rather than stacking into fatigue.
 */
function pickMotif(ctx: MotifContext, prev: MotifKind | null): TerrainMotif {
  if (
    (prev === 'funnel' || prev === 'sweep' || (prev === 'straight' && ctx.intensity > 0.7)) &&
    ctx.rng() < 0.45
  ) {
    return restMotif(ctx);
  }
  const weights: Array<{ kind: MotifKind; weight: number }> = [
    { kind: 'rest', weight: lerp(3, 0.6, ctx.intensity) },
    { kind: 'straight', weight: lerp(1.2, 2.2, ctx.intensity) },
    { kind: 'funnel', weight: lerp(0.4, 2, ctx.intensity) },
    { kind: 'sweep', weight: lerp(1.5, 1.6, ctx.intensity) },
    { kind: 'stairs', weight: lerp(0.6, 1.4, ctx.intensity) },
  ];
  let total = 0;
  for (const w of weights) {
    if (w.kind === prev) {
      // Suppress immediate repeats so the corridor never holds one shape
      // twice in a row (which reads as the old directionless wander).
      w.weight = 0;
    }
    total += w.weight;
  }
  let roll = ctx.rng() * total;
  let chosen: MotifKind = 'rest';
  for (const w of weights) {
    roll -= w.weight;
    // Skip the suppressed (zero-weight) prev kind even when `roll` starts at
    // exactly 0, so a repeat is never selected on the boundary roll.
    if (w.weight > 0 && roll <= 0) {
      chosen = w.kind;
      break;
    }
  }
  switch (chosen) {
    case 'straight':
      return straightMotif(ctx);
    case 'funnel':
      return funnelMotif(ctx);
    case 'sweep':
      return sweepMotif(ctx);
    case 'stairs':
      return stairsMotif(ctx);
    default:
      return restMotif(ctx);
  }
}

/**
 * Generate the winding-corridor terrain deterministically from a seed
 * (slug + variant). A motif sequencer (see {@link MotifKind}) drives the
 * midline and breathing half-width through a series of readable wave
 * shapes paced by {@link wavesIntensityAt}; sparse vertical midline steps
 * become lethal walls (an up-shift raises the floor into a lethal floor
 * wall, a down-shift drops the ceiling into a lethal ceiling wall).
 *
 * DETERMINISM CONTRACT (two parts):
 *   - SQUEEZE (continuous): the floor and ceiling may ramp by DIFFERENT
 *     deltas (the corridor changes width), but the per-step slope budget
 *     is split between the midline and the width ramp so EACH surface's
 *     slope stays `<= WAVES_RAMP_SLOPE_FRAC * vY` (well under the
 *     validator's `<= vY` cap). On continuous ramps the glide-clamp is a
 *     closed-form function of beat, so squeezing is resolution-
 *     independent with no re-anchor needed.
 *   - WALLS (discontinuous): every vertical step still shifts floor AND
 *     ceiling by the SAME delta (width held constant across the step), so
 *     each discontinuity carries exactly one lethal wall that the engine
 *     re-anchors on. Changing width AT a wall would split it into two
 *     mismatched faces / a lone non-lethal step and silently break replay
 *     determinism (see the re-anchor INVARIANT note in
 *     `engine/waves/core.ts`) — so width only ever changes on ramps.
 */
export function generateWavesTerrain(
  seed: string,
  variant: Variant,
  totalBeats: number,
  vY: number,
): WavesTerrain {
  const halfWide = WAVES_CORRIDOR_HALF_WIDE;
  const halfNarrow = WAVES_CORRIDOR_HALF_NARROW[variant];
  const minWidth = 2 * WAVES_VARIANT_TUNING[variant].playerRadius + WAVES_MIN_CORRIDOR_MARGIN;
  const maxSlope = WAVES_RAMP_SLOPE_FRAC * vY;
  const midSlopeMax = maxSlope * WAVES_MID_SLOPE_BUDGET_FRAC;
  const widthSlopeMax = maxSlope * WAVES_WIDTH_SLOPE_BUDGET_FRAC;
  const cadence = WAVES_TERRAIN_CADENCE_BEATS;
  const wallTuning = WAVES_WALL_TUNING[variant];
  const approach = wallApproachBeats(vY);
  const rng = mulberry32(hashStringToSeed(`${seed}:${variant}:terrain`));

  const floor: WavesTerrainNode[] = [];
  const ceiling: WavesTerrainNode[] = [];
  // Start fully open so the spawn + intro read clearly before the first
  // squeeze.
  let half = halfWide;
  let mid = clamp(WAVES_DEFAULT_SPAWN_Y, WAVES_CEILING_Y + half, WAVES_FLOOR_Y - half);
  floor.push({ beat: 0, y: mid + half });
  ceiling.push({ beat: 0, y: mid - half });

  let motif: TerrainMotif | null = null;
  let motifBeatsLeft = 0;
  let motifStepIndex = 0;
  let sweepDir = 1;
  let prevKind: MotifKind | null = null;

  let beat = 0;
  let lastWallBeat = -Infinity;
  while (beat < totalBeats - 1e-6) {
    const step = Math.min(cadence, totalBeats - beat);
    const nextBeat = beat + step;

    // Re-pick a motif when the current rhythm group expires. The motif
    // commits to a sustained shape (a held sliver, a converging funnel, a
    // sweep) rather than jittering a fresh random target each cadence.
    if (motif === null || motifBeatsLeft <= 0) {
      const ctx: MotifContext = {
        rng,
        intensity: wavesIntensityAt(beat, totalBeats),
        halfWide,
        halfNarrow,
        half,
        mid,
        sweepDir,
        cadence,
      };
      motif = pickMotif(ctx, prevKind);
      if (motif.kind === 'sweep') {
        // Alternate sweep direction so a run of sweeps carves an S rather
        // than repeatedly hugging the same surface.
        sweepDir = -sweepDir;
      }
      motifBeatsLeft = motif.durationBeats;
      motifStepIndex = 0;
      prevKind = motif.kind;
    }

    // durationBeats is always >= 2 cadence steps, so the division is safe.
    const motifProgress = 1 - motifBeatsLeft / motif.durationBeats;

    // Width target: only a funnel ramps across the motif; the rest hold a
    // constant target (the per-step clamp still bounds the rate).
    const targetHalf =
      motif.kind === 'funnel'
        ? lerp(motif.widthFrom, motif.widthTo, motifProgress)
        : motif.widthTo;
    const dHalf = clamp(targetHalf - half, -widthSlopeMax * step, widthSlopeMax * step);
    half = clamp(half + dHalf, halfNarrow, halfWide);

    const midMin = WAVES_CEILING_Y + half;
    const midMax = WAVES_FLOOR_Y - half;
    let midTarget: number;
    if (motif.midMode === 'hold') {
      midTarget = motif.midHold;
    } else if (motif.midMode === 'stairs') {
      // Tread every two cadence steps (ramp, flat, ramp, flat …) so the
      // climb reads as discrete steps rather than one long diagonal.
      const tread = Math.floor((motifStepIndex + 1) / 2);
      midTarget = motif.midBase + motif.stairDir * motif.stairHeight * tread;
    } else {
      midTarget = motif.midTarget;
    }
    midTarget = clamp(midTarget, midMin, midMax);
    const dMid = clamp(midTarget - mid, -midSlopeMax * step, midSlopeMax * step);
    mid = clamp(mid + dMid, midMin, midMax);

    floor.push({ beat: nextBeat, y: mid + half });
    ceiling.push({ beat: nextBeat, y: mid - half });
    beat = nextBeat;
    motifBeatsLeft -= step;
    motifStepIndex += 1;

    const eligible =
      beat - lastWallBeat >= wallTuning.minSpacingBeats &&
      beat >= approach &&
      beat <= totalBeats - WAVES_WALL_RECOVERY_BEATS - cadence;
    if (eligible && rng() < wallTuning.wallProb) {
      const rolled =
        wallTuning.minHeight + rng() * (wallTuning.maxHeight - wallTuning.minHeight);
      // A wall holds width constant (both surfaces jump by the same delta
      // → exactly one lethal face). Terrain width is sampled
      // left-continuously, so the validator pairs the post-step node with
      // the PRE-step opposite surface, making the apparent corridor at the
      // step `2*half - height`. Cap the height so that stays above the
      // playable minimum — which also means a squeezed corridor (small
      // `half`) simply can't host a wall, so walls land in the open
      // stretches where there's room to climb the face.
      const maxWallHeight = 2 * half - minWidth - 4;
      const height = Math.min(rolled, maxWallHeight);
      const dir = rng() < 0.5 ? -1 : 1;
      const newMid = clamp(mid + dir * height, WAVES_CEILING_Y + half, WAVES_FLOOR_Y - half);
      if (Math.abs(newMid - mid) >= WAVES_MIN_WALL_HEIGHT) {
        floor.push({ beat, y: newMid + half });
        ceiling.push({ beat, y: newMid - half });
        mid = newMid;
        lastWallBeat = beat;
        // Re-pick a motif from the new level next step so the shape reads
        // as deliberately resuming above/below the wall rather than the
        // pre-wall glide fighting the step.
        motifBeatsLeft = 0;
      }
    }
  }
  return { floor, ceiling };
}
